import * as React from "react";
import { CohortRetentionTableProps, CohortData } from "../../types/stripe";
import { AnalyticsTableSpinner, AnalyticsTooltip } from "../shared";

/**
 * CohortRetentionTable Component
 * 
 * Cohort retention heat map displaying customer retention rates over time periods
 */
const CohortRetentionTable: React.FC<CohortRetentionTableProps> = ({
  cohorts,
  onCohortClick,
  colorScale = ['#fee5d9', '#fcae91', '#fb6a4a', '#de2d26', '#a50f15'],
  isLoading = false,
  className = "",
  id
}) => {
  const [hoveredCell, setHoveredCell] = React.useState<{
    cohortIndex: number;
    periodIndex: number;
  } | null>(null);

  // Get the maximum number of periods across all cohorts
  const maxPeriods = React.useMemo(() => {
    return cohorts.reduce((max, cohort) => 
      Math.max(max, cohort.retention_rates.length), 0
    );
  }, [cohorts]);

  // Generate color intensity based on retention rate
  const getRetentionColor = (rate: number): string => {
    if (rate === 0) return '#f8f9fa';
    
    const intensity = Math.min(rate / 100, 1); // Normalize to 0-1
    const colorIndex = Math.floor(intensity * (colorScale.length - 1));
    return colorScale[colorIndex];
  };

  // Format retention rate for display
  const formatRetentionRate = (rate: number): string => {
    return `${rate.toFixed(1)}%`;
  };

  // Calculate average retention for each period
  const averageRetentionByPeriod = React.useMemo(() => {
    const averages = [];
    for (let period = 0; period < maxPeriods; period++) {
      const validRates = cohorts
        .filter(cohort => cohort.retention_rates[period] !== undefined)
        .map(cohort => cohort.retention_rates[period]);
      
      if (validRates.length > 0) {
        const average = validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
        averages.push(average);
      } else {
        averages.push(0);
      }
    }
    return averages;
  }, [cohorts, maxPeriods]);

  // Calculate cohort sizes summary
  const cohortSummary = React.useMemo(() => {
    const totalCustomers = cohorts.reduce((sum, cohort) => sum + cohort.cohort_size, 0);
    const averageCohortSize = cohorts.length > 0 ? totalCustomers / cohorts.length : 0;
    const largestCohort = Math.max(...cohorts.map(c => c.cohort_size));
    const smallestCohort = Math.min(...cohorts.map(c => c.cohort_size));

    return {
      totalCustomers,
      averageCohortSize,
      largestCohort,
      smallestCohort,
      cohortCount: cohorts.length
    };
  }, [cohorts]);

  const handleCellClick = (cohort: CohortData, periodIndex: number) => {
    if (onCohortClick) {
      onCohortClick(cohort);
    }
  };

  const getTooltipContent = (cohort: CohortData, periodIndex: number): string => {
    const retentionRate = cohort.retention_rates[periodIndex];
    const retainedCustomers = Math.round((cohort.cohort_size * retentionRate) / 100);
    
    return [
      `Cohort: ${cohort.cohort_month}`,
      `Period: ${periodIndex} month${periodIndex !== 1 ? 's' : ''}`,
      `Retention: ${formatRetentionRate(retentionRate)}`,
      `Customers: ${retainedCustomers}/${cohort.cohort_size}`,
      `Original Size: ${cohort.cohort_size}`
    ].join('\n');
  };

  if (isLoading) {
    return (
      <div className={`cohort-retention-table ${className}`.trim()} id={id}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-table me-2" aria-hidden="true"></i>
              Cohort Retention Analysis
            </h6>
          </div>
          <div className="card-body">
            <AnalyticsTableSpinner message="Loading cohort data..." />
          </div>
        </div>
      </div>
    );
  }

  if (!cohorts || cohorts.length === 0) {
    return (
      <div className={`cohort-retention-table ${className}`.trim()} id={id}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-table me-2" aria-hidden="true"></i>
              Cohort Retention Analysis
            </h6>
          </div>
          <div className="card-body">
            <div className="text-center py-5 text-muted">
              <i className="fas fa-users fa-2x mb-3" aria-hidden="true"></i>
              <p className="mb-0">No cohort data available</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`cohort-retention-table ${className}`.trim()} id={id}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-table me-2" aria-hidden="true"></i>
              Cohort Retention Analysis
            </h6>
            
            {/* Summary Stats */}
            <div className="d-flex gap-3 text-sm">
              <span className="text-muted">
                <strong>{cohortSummary.cohortCount}</strong> cohorts
              </span>
              <span className="text-muted">
                <strong>{cohortSummary.totalCustomers.toLocaleString()}</strong> total customers
              </span>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0 cohort-table">
              <thead className="table-light">
                <tr>
                  <th className="cohort-header" style={{ minWidth: '120px' }}>
                    Cohort Month
                  </th>
                  <th className="text-center" style={{ minWidth: '80px' }}>
                    Size
                  </th>
                  {Array.from({ length: maxPeriods }, (_, index) => (
                    <th key={index} className="text-center period-header">
                      {index === 0 ? 'Month 0' : `Month ${index}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohorts.map((cohort, cohortIndex) => (
                  <tr key={cohort.cohort_month}>
                    <td className="fw-bold cohort-month">
                      {new Date(cohort.cohort_month).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short'
                      })}
                    </td>
                    <td className="text-center fw-bold">
                      {cohort.cohort_size.toLocaleString()}
                    </td>
                    {Array.from({ length: maxPeriods }, (_, periodIndex) => {
                      const retentionRate = cohort.retention_rates[periodIndex];
                      const hasData = retentionRate !== undefined;
                      
                      return (
                        <td
                          key={periodIndex}
                          className={`text-center cohort-cell ${onCohortClick && hasData ? 'clickable' : ''}`}
                          style={{
                            backgroundColor: hasData ? getRetentionColor(retentionRate) : '#f8f9fa',
                            color: hasData && retentionRate > 50 ? 'white' : 'inherit'
                          }}
                          onClick={() => hasData && handleCellClick(cohort, periodIndex)}
                          onMouseEnter={() => hasData && setHoveredCell({ cohortIndex, periodIndex })}
                          onMouseLeave={() => setHoveredCell(null)}
                        >
                          {hasData ? (
                            <AnalyticsTooltip
                              content={getTooltipContent(cohort, periodIndex)}
                              placement="top"
                            >
                              <span className="retention-value">
                                {formatRetentionRate(retentionRate)}
                              </span>
                            </AnalyticsTooltip>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                
                {/* Average row */}
                <tr className="table-secondary">
                  <td className="fw-bold">
                    <i className="fas fa-chart-bar me-1" aria-hidden="true"></i>
                    Average
                  </td>
                  <td className="text-center fw-bold">
                    {Math.round(cohortSummary.averageCohortSize).toLocaleString()}
                  </td>
                  {averageRetentionByPeriod.map((average, index) => (
                    <td key={index} className="text-center fw-bold">
                      {average > 0 ? formatRetentionRate(average) : '-'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Color Legend */}
        <div className="card-footer bg-light">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <span className="small text-muted me-3">Retention Rate:</span>
                <div className="d-flex">
                  <div
                    className="legend-color-box me-1"
                    style={{ backgroundColor: '#f8f9fa' }}
                    title="No data"
                  ></div>
                  {colorScale.map((color, index) => (
                    <div
                      key={color}
                      className="legend-color-box me-1"
                      style={{ backgroundColor: color }}
                      title={`${index * 20}-${(index + 1) * 20}%`}
                    ></div>
                  ))}
                </div>
                <span className="small text-muted ms-2">0% → 100%</span>
              </div>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="row text-center text-md-end">
                <div className="col-6 col-md-3">
                  <div className="small">
                    <div className="fw-bold">{cohortSummary.largestCohort.toLocaleString()}</div>
                    <div className="text-muted">Largest</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="small">
                    <div className="fw-bold">{cohortSummary.smallestCohort.toLocaleString()}</div>
                    <div className="text-muted">Smallest</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="small">
                    <div className="fw-bold">{Math.round(cohortSummary.averageCohortSize).toLocaleString()}</div>
                    <div className="text-muted">Avg Size</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="small">
                    <div className="fw-bold">{maxPeriods}</div>
                    <div className="text-muted">Max Periods</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .cohort-retention-table .cohort-table {
          font-size: 0.875rem;
        }

        .cohort-retention-table .cohort-header {
          background-color: var(--bs-primary);
          color: white;
          font-weight: 600;
        }

        .cohort-retention-table .period-header {
          min-width: 70px;
          font-weight: 600;
        }

        .cohort-retention-table .cohort-month {
          background-color: rgba(var(--bs-primary-rgb), 0.1);
          white-space: nowrap;
        }

        .cohort-retention-table .cohort-cell {
          position: relative;
          transition: all 0.2s ease;
          min-width: 70px;
          padding: 8px 4px;
        }

        .cohort-retention-table .cohort-cell.clickable {
          cursor: pointer;
        }

        .cohort-retention-table .cohort-cell.clickable:hover {
          transform: scale(1.05);
          border: 2px solid var(--bs-primary);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          z-index: 10;
        }

        .cohort-retention-table .retention-value {
          font-weight: 600;
          font-size: 0.8rem;
        }

        .cohort-retention-table .legend-color-box {
          width: 20px;
          height: 15px;
          border: 1px solid #dee2e6;
          border-radius: 2px;
        }

        .cohort-retention-table .card-header {
          background-color: rgba(var(--bs-info-rgb), 0.05);
          border-bottom: 1px solid rgba(var(--bs-info-rgb), 0.15);
        }

        .cohort-retention-table .table-secondary {
          background-color: rgba(var(--bs-secondary-rgb), 0.1);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .cohort-retention-table .cohort-table {
            font-size: 0.75rem;
          }

          .cohort-retention-table .period-header {
            min-width: 60px;
            font-size: 0.7rem;
          }

          .cohort-retention-table .cohort-cell {
            padding: 4px 2px;
          }

          .cohort-retention-table .retention-value {
            font-size: 0.7rem;
          }

          .cohort-retention-table .legend-color-box {
            width: 15px;
            height: 12px;
          }
        }

        /* Print styles */
        @media print {
          .cohort-retention-table .cohort-cell.clickable:hover {
            transform: none;
            border: 1px solid #dee2e6;
            box-shadow: none;
          }
        }
      `}</style>
    </div>
  );
};

CohortRetentionTable.displayName = 'CohortRetentionTable';

export default CohortRetentionTable;